import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  createUserWithEmailAndPassword,
  getMultiFactorResolver,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  TotpMultiFactorGenerator,
  updateProfile,
  type MultiFactorError,
  type MultiFactorResolver,
  type User as FirebaseUser,
} from 'firebase/auth';
import type { MeClientSummary, User } from '@keobook/shared';
import { ApiError, api, setAuthTokenProvider, setClientIdProvider } from './api';
import { setChatStreamTokenProvider } from './chat-stream';
import { firebaseAuth, firebaseEnabled } from './firebase';

/**
 * Auth + onboarding state. Two modes behind one interface:
 *
 *  - IdP mode (firebaseEnabled): real Google Identity Platform. Signup/login/
 *    reset go through the Firebase SDK; the API token is a fresh Firebase ID
 *    token. The app_user is auto-provisioned by the API on first /me.
 *  - dev mode: email/password stub against /v1/auth/session (HS256 dev JWT),
 *    persisted in localStorage. Lets local dev run without the IdP.
 */
const TOKEN_KEY = 'kb_dev_access_token';
const DEMO_KEY = 'kb_demo_token';
const CURRENT_CLIENT_KEY = 'kb_current_client';

export type AuthStatus = 'unknown' | 'unauthenticated' | 'authenticated';

/** signIn resolves to 'mfa-required' when a TOTP challenge must be answered. */
export type SignInResult = 'ok' | 'mfa-required';

export type AuthContextValue = {
  status: AuthStatus;
  user: User | null;
  clients: MeClientSummary[];
  currentClient: MeClientSummary | null;
  /** Switch which client (business) the user is acting on — for accountants
   *  linked to more than one. */
  setCurrentClient: (clientId: string) => void;
  emailVerified: boolean;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  /** Complete a pending TOTP challenge from signIn() === 'mfa-required'. */
  resolveMfa: (code: string) => Promise<void>;
  /** Display name of the factor awaiting a challenge, if any. */
  mfaHint: string | null;
  signUp: (email: string, password: string, fullName: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  resendVerification: () => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
  error: string | null;
  /** True when the active session is from the public "Try the demo" flow. */
  isDemo: boolean;
  /** Drop the demo session and bounce the user back to the marketing site. */
  exitDemo: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('unknown');
  const [user, setUser] = useState<User | null>(null);
  const [clients, setClients] = useState<MeClientSummary[]>([]);
  // Which client the user is acting on (for accountants linked to several).
  const [currentClientId, setCurrentClientId] = useState<string | null>(() => {
    try {
      return typeof window === 'undefined' ? null : window.localStorage.getItem(CURRENT_CLIENT_KEY);
    } catch {
      return null;
    }
  });
  const currentClientIdRef = useRef<string | null>(currentClientId);
  const [emailVerified, setEmailVerified] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [mfaHint, setMfaHint] = useState<string | null>(null);

  // Dev-mode token, persisted. Unused in IdP mode.
  const devTokenRef = useRef<string | null>(null);
  // Demo-mode token from the public "Try the demo" flow. Takes precedence over
  // both IdP and dev mode when present. Read synchronously in the lazy state
  // initializer so isDemo is correct on render 0 — otherwise the IdP effect
  // registers an onAuthStateChanged listener that fires with fbUser=null
  // before isDemo flips, momentarily setting status='unauthenticated' and
  // bouncing the user to /signin (the bug Try-the-demo had).
  const demoTokenRef = useRef<string | null>(
    typeof window === 'undefined'
      ? null
      : (() => {
          try {
            return window.localStorage.getItem(DEMO_KEY);
          } catch {
            return null;
          }
        })(),
  );
  const [isDemo, setIsDemo] = useState<boolean>(() => !!demoTokenRef.current);
  // Pending TOTP challenge between signIn() and resolveMfa(). IdP mode only.
  const mfaResolverRef = useRef<MultiFactorResolver | null>(null);

  // Register how the API client obtains a bearer token. Wires the chat SSE
  // helper to the same provider — it lives in its own module so the SSE
  // path can fetch a token without pulling in the whole api.ts surface.
  useEffect(() => {
    if (demoTokenRef.current) {
      const provider = async () => demoTokenRef.current;
      setAuthTokenProvider(provider);
      setChatStreamTokenProvider(provider);
      return;
    }
    const auth = firebaseAuth;
    if (firebaseEnabled && auth) {
      const provider = async () => {
        const u = auth.currentUser;
        return u ? u.getIdToken() : null;
      };
      setAuthTokenProvider(provider);
      setChatStreamTokenProvider(provider);
    } else {
      try {
        devTokenRef.current = localStorage.getItem(TOKEN_KEY);
      } catch {
        /* ignore */
      }
      const devProvider = async () => devTokenRef.current;
      setAuthTokenProvider(devProvider);
      setChatStreamTokenProvider(devProvider);
    }
  }, []);

  // --- demo mode: bootstrap from stored token ---
  useEffect(() => {
    if (!isDemo) return;
    let alive = true;
    loadMe()
      .then(() => {
        if (alive) setStatus('authenticated');
      })
      .catch(() => {
        // The /demo entry page navigates to /dashboard while this /me is in
        // flight; the page-unload aborts the fetch and this catch fires AS
        // PART OF the unload. If we let it run, we'd wipe the just-stored
        // demo token before the next page can read it. `!alive` means the
        // component already unmounted — leave localStorage alone.
        if (!alive) return;
        try {
          localStorage.removeItem(DEMO_KEY);
        } catch {
          /* ignore */
        }
        demoTokenRef.current = null;
        setIsDemo(false);
        setStatus('unauthenticated');
      });
    return () => {
      alive = false;
    };
    // loadMe is stable from useCallback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemo]);

  const loadMe = useCallback(async () => {
    const me = await api.getMe();
    setUser(me.user);
    setClients(me.clients);
  }, []);

  // The effective current client: the selected one, else the first available.
  const currentClient = useMemo(
    () => clients.find((c) => c.id === currentClientId) ?? clients[0] ?? null,
    [clients, currentClientId],
  );

  // Keep the API's X-Client-Id provider in sync SYNCHRONOUSLY during render, not
  // in an effect. Pages fire their first fetch from effects that run BEFORE this
  // provider's effects (React runs child effects before parent effects), so an
  // effect here would let them send a stale persisted id → tenant-scoped routes
  // 404 ("Client not found"). Once clients are loaded, `currentClient` is always
  // one the user actually has (find(persisted) ?? clients[0]); before they load
  // we keep the persisted id for the reload fast-path. GuidedRoute holds pages
  // until the bootstrap /v1/me populates clients, so a page's first mount always
  // sees a validated id here.
  currentClientIdRef.current = clients.length > 0 ? (currentClient?.id ?? null) : currentClientId;

  // Persist the reconciled selection + self-heal a stale/inaccessible id (a
  // client id left in localStorage from a prior session that isn't this user's).
  useEffect(() => {
    if (clients.length === 0) return;
    const effectiveId = currentClient?.id ?? null;
    if (effectiveId !== currentClientId) setCurrentClientId(effectiveId);
    try {
      if (effectiveId) window.localStorage.setItem(CURRENT_CLIENT_KEY, effectiveId);
      else window.localStorage.removeItem(CURRENT_CLIENT_KEY);
    } catch {
      /* ignore */
    }
  }, [currentClient, currentClientId]);

  useEffect(() => {
    setClientIdProvider(() => currentClientIdRef.current);
  }, []);

  const setCurrentClient = useCallback((clientId: string) => {
    // Persist + update the request provider synchronously so a hard navigation
    // right after (to re-scope all page data) reads the new client.
    try {
      window.localStorage.setItem(CURRENT_CLIENT_KEY, clientId);
    } catch {
      /* ignore */
    }
    currentClientIdRef.current = clientId;
    setCurrentClientId(clientId);
  }, []);

  // --- IdP mode: react to Firebase auth state ---
  useEffect(() => {
    if (isDemo) return;
    const auth = firebaseAuth;
    if (!firebaseEnabled || !auth) return;
    const unsub = onAuthStateChanged(auth, async (fbUser: FirebaseUser | null) => {
      if (!fbUser) {
        setUser(null);
        setClients([]);
        setEmailVerified(true);
        setStatus('unauthenticated');
        return;
      }
      setEmailVerified(fbUser.emailVerified);
      try {
        await loadMe();
        setStatus('authenticated');
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          await fbSignOut(auth);
        }
        setStatus('unauthenticated');
      }
    });
    return () => unsub();
  }, [loadMe]);

  // --- dev mode: bootstrap from stored token ---
  useEffect(() => {
    if (firebaseEnabled || isDemo) return;
    const t = devTokenRef.current;
    if (!t) {
      setStatus('unauthenticated');
      return;
    }
    let alive = true;
    loadMe()
      .then(() => alive && setStatus('authenticated'))
      .catch((err: unknown) => {
        if (!alive) return;
        if (err instanceof ApiError && err.status === 401) {
          try {
            localStorage.removeItem(TOKEN_KEY);
          } catch {
            /* ignore */
          }
          devTokenRef.current = null;
        }
        setStatus('unauthenticated');
      });
    return () => {
      alive = false;
    };
  }, [loadMe]);

  const signIn = useCallback(
    async (email: string, password: string): Promise<SignInResult> => {
      setError(null);
      setMfaHint(null);
      mfaResolverRef.current = null;
      try {
        if (firebaseEnabled && firebaseAuth) {
          const cred = await signInWithEmailAndPassword(firebaseAuth, email, password);
          // Don't wait for onAuthStateChanged. It will fire shortly after
          // this resolves, but the caller (SignIn.onSubmit) is about to
          // call navigate('/') *immediately* — and GuidedRoute reads
          // `status` synchronously. If status is still 'unauthenticated'
          // when the navigation lands, the guard bounces us back to
          // /signin (the "first attempt fails, second succeeds" bug).
          //
          // Pre-flip status here. onAuthStateChanged will re-run loadMe
          // a moment later, which is idempotent — no harm done.
          setEmailVerified(cred.user.emailVerified);
          try {
            await loadMe();
            setStatus('authenticated');
          } catch (err) {
            if (err instanceof ApiError && err.status === 401) {
              await fbSignOut(firebaseAuth);
            }
            setStatus('unauthenticated');
            throw err;
          }
          return 'ok';
        }
        const res = await api.devSignIn(email, password);
        try {
          localStorage.setItem(TOKEN_KEY, res.accessToken);
        } catch {
          /* ignore */
        }
        devTokenRef.current = res.accessToken;
        await loadMe();
        setStatus('authenticated');
        return 'ok';
      } catch (err: unknown) {
        // A TOTP-protected account throws here; capture the resolver and hand
        // the UI a challenge instead of surfacing it as an error.
        if (
          firebaseEnabled &&
          firebaseAuth &&
          (err as { code?: string }).code === 'auth/multi-factor-auth-required'
        ) {
          const resolver = getMultiFactorResolver(firebaseAuth, err as MultiFactorError);
          mfaResolverRef.current = resolver;
          setMfaHint(resolver.hints[0]?.displayName ?? 'your authenticator app');
          return 'mfa-required';
        }
        const message = mapAuthError(err);
        setError(message);
        throw new Error(message);
      }
    },
    [loadMe],
  );

  const resolveMfa = useCallback(
    async (code: string) => {
      const resolver = mfaResolverRef.current;
      if (!resolver) throw new Error('No pending two-factor challenge.');
      setError(null);
      try {
        const hint = resolver.hints[0];
        if (!hint) throw new Error('No pending two-factor challenge.');
        const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code.trim());
        const cred = await resolver.resolveSignIn(assertion);
        mfaResolverRef.current = null;
        setMfaHint(null);
        // Same race fix as signIn() — pre-flip status so the SignIn
        // page's navigate('/') doesn't race the onAuthStateChanged
        // listener.
        const auth = firebaseAuth;
        if (auth) {
          setEmailVerified(cred.user.emailVerified);
          try {
            await loadMe();
            setStatus('authenticated');
          } catch (err) {
            if (err instanceof ApiError && err.status === 401) {
              await fbSignOut(auth);
            }
            setStatus('unauthenticated');
            throw err;
          }
        }
      } catch (err: unknown) {
        const message = mapAuthError(err);
        setError(message);
        throw new Error(message);
      }
    },
    [loadMe],
  );

  const signUp = useCallback(
    async (email: string, password: string, fullName: string) => {
      setError(null);
      try {
        if (firebaseEnabled && firebaseAuth) {
          const cred = await createUserWithEmailAndPassword(firebaseAuth, email, password);
          if (fullName) await updateProfile(cred.user, { displayName: fullName });
          // Refresh BEFORE the API call so the bearer token carries the name.
          // The verification email itself comes from our Brevo sender
          // (api.sendVerificationEmail), not Firebase's default — full brand
          // control, "from" matches the welcome email. Best-effort; we don't
          // block signup on a transient email failure.
          await cred.user.getIdToken(true);
          try {
            await api.sendVerificationEmail();
          } catch (sendErr) {
            console.warn('verification email send failed', sendErr);
          }
          // onAuthStateChanged handles /me + status.
        } else {
          const res = await api.devSignIn(email, password, fullName);
          try {
            localStorage.setItem(TOKEN_KEY, res.accessToken);
          } catch {
            /* ignore */
          }
          devTokenRef.current = res.accessToken;
          await loadMe();
          setStatus('authenticated');
        }
      } catch (err: unknown) {
        const message = mapAuthError(err);
        setError(message);
        throw new Error(message);
      }
    },
    [loadMe],
  );

  const resetPassword = useCallback(async (email: string) => {
    // Switched from Firebase sendPasswordResetEmail() to our API in 2026-06.
    // The link in the email now points at app.keobooks.com and the sender is
    // hello@keobooks.com (via Brevo), not the default firebaseapp.com one
    // that looked like phishing to a CPA. The endpoint is anonymous and
    // always 202s so we don't leak account existence.
    if (firebaseEnabled) {
      await api.sendPasswordResetEmail(email);
    }
    // dev mode: no-op (no real passwords).
  }, []);

  const resendVerification = useCallback(async () => {
    // Same switch — Settings' "Resend verification email" goes through Brevo
    // now via our /v1/auth/email/send-verification endpoint. Requires the
    // user to be signed in (the route reads email from the JWT, not body,
    // so it can't be abused to spam other addresses).
    if (firebaseEnabled && firebaseAuth?.currentUser) {
      await api.sendVerificationEmail();
    }
  }, []);

  const signOut = useCallback(async () => {
    if (demoTokenRef.current) {
      // Demo signOut == exit demo (clears the demo token, drops the session).
      try {
        localStorage.removeItem(DEMO_KEY);
      } catch {
        /* ignore */
      }
      demoTokenRef.current = null;
      setIsDemo(false);
      setUser(null);
      setClients([]);
      setStatus('unauthenticated');
    } else if (firebaseEnabled && firebaseAuth) {
      await fbSignOut(firebaseAuth);
    } else {
      try {
        localStorage.removeItem(TOKEN_KEY);
      } catch {
        /* ignore */
      }
      devTokenRef.current = null;
      setUser(null);
      setClients([]);
      setStatus('unauthenticated');
    }
    setError(null);
  }, []);

  const exitDemo = useCallback(() => {
    try {
      localStorage.removeItem(DEMO_KEY);
    } catch {
      /* ignore */
    }
    demoTokenRef.current = null;
    // Hard navigation back to the marketing site clears any stale state and
    // gets the user out of the dashboard without a flash.
    window.location.href = '/';
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      clients,
      currentClient,
      setCurrentClient,
      emailVerified,
      signIn,
      resolveMfa,
      mfaHint,
      signUp,
      resetPassword,
      resendVerification,
      signOut,
      refresh: loadMe,
      error,
      isDemo,
      exitDemo,
    }),
    [
      status,
      user,
      clients,
      currentClient,
      setCurrentClient,
      emailVerified,
      signIn,
      resolveMfa,
      mfaHint,
      signUp,
      resetPassword,
      resendVerification,
      signOut,
      loadMe,
      error,
      isDemo,
      exitDemo,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

/** Turn Firebase / API errors into human messages. */
function mapAuthError(err: unknown): string {
  const code = (err as { code?: string }).code;
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Incorrect email or password.';
    case 'auth/email-already-in-use':
      return 'An account with that email already exists. Try signing in.';
    case 'auth/weak-password':
      return 'Password is too weak — use at least 8 characters.';
    case 'auth/invalid-email':
      return 'That email address looks invalid.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a moment and try again.';
    case 'auth/invalid-verification-code':
    case 'auth/missing-verification-code':
      return 'That code is incorrect or expired. Open your authenticator app and try the current code.';
    case 'auth/operation-not-allowed':
      return 'Two-factor authentication is not enabled for this project yet.';
    default:
      return err instanceof Error ? err.message : 'Authentication failed.';
  }
}
